import React, { useReducer } from 'react';
import TicketValidator from './TicketValidator';
import CustomerRegistration from './CustomerRegistration';
import SaleDetails from './SaleDetails';
import InvoiceGenerator from './InvoiceGenerator';
import DeliveryOptions from './DeliveryOptions';
import { getConfig } from '../utils/config';

// Flow state constants
const STEPS = {
    TICKET_VALIDATION: 'ticket-validation',
    CUSTOMER_REGISTRATION: 'customer-registration',
    SALE_DETAILS: 'sale-details',
    INVOICE_GENERATION: 'invoice-generation',
    DELIVERY_OPTIONS: 'delivery-options',
    COMPLETED: 'completed'
};

const ACTIONS = {
    SET_TICKET: 'SET_TICKET',
    SET_CUSTOMER: 'SET_CUSTOMER',
    SET_SALE: 'SET_SALE',
    SET_INVOICE: 'SET_INVOICE',
    SET_DELIVERY_METHOD: 'SET_DELIVERY_METHOD',
    NEXT_STEP: 'NEXT_STEP',
    PREVIOUS_STEP: 'PREVIOUS_STEP',
    SET_STEP: 'SET_STEP',
    SET_ERROR: 'SET_ERROR',
    CLEAR_ERROR: 'CLEAR_ERROR',
    RESET_FLOW: 'RESET_FLOW'
};

// Initial state for the invoice flow
const initialState = {
    step: STEPS.TICKET_VALIDATION,
    customer: null,
    ticket: null,
    sale: null,
    invoice: null,
    deliveryMethod: null,
    isExistingInvoice: false,
    hasExistingCustomer: false,
    error: null,
    loading: false
};

// Reducer to manage the complex flow state
function flowReducer(state, action) {
    switch (action.type) {
        case ACTIONS.SET_TICKET:
            return {
                ...state,
                ticket: action.payload.ticket,
                isExistingInvoice: action.payload.isExistingInvoice,
                hasExistingCustomer: action.payload.hasExistingCustomer || false,
                customer: action.payload.existingCustomer || null,
                error: null
            };
        
        case ACTIONS.SET_CUSTOMER:
            return {
                ...state,
                customer: action.payload,
                error: null
            };
        
        case ACTIONS.SET_SALE:
            return {
                ...state,
                sale: action.payload,
                error: null
            };
        
        case ACTIONS.SET_INVOICE:
            return {
                ...state,
                invoice: action.payload,
                error: null
            };
        
        case ACTIONS.SET_DELIVERY_METHOD:
            return {
                ...state,
                deliveryMethod: action.payload,
                error: null
            };
        
        case ACTIONS.NEXT_STEP:
            return {
                ...state,
                step: getNextStep(state.step, state.isExistingInvoice, state.hasExistingCustomer),
                error: null
            };
        
        case ACTIONS.PREVIOUS_STEP:
            return {
                ...state,
                step: getPreviousStep(state.step, state.isExistingInvoice, state.hasExistingCustomer),
                error: null
            };
        
        case ACTIONS.SET_STEP:
            return {
                ...state,
                step: action.payload,
                error: null
            };
        
        case ACTIONS.SET_ERROR:
            return {
                ...state,
                error: action.payload,
                loading: false
            };
        
        case ACTIONS.CLEAR_ERROR:
            return {
                ...state,
                error: null
            };
        
        case ACTIONS.RESET_FLOW:
            return initialState;
        
        default:
            return state;
    }
}

// Helper function to determine next step based on scenario
function getNextStep(currentStep, isExistingInvoice, hasExistingCustomer) {
    if (isExistingInvoice) {
        // Scenario 2: Existing Invoice Flow
        switch (currentStep) {
            case STEPS.TICKET_VALIDATION:
                return STEPS.DELIVERY_OPTIONS;
            case STEPS.DELIVERY_OPTIONS:
                return STEPS.COMPLETED;
            default:
                return currentStep;
        }
    } else {
        // Scenario 1: New Invoice Flow
        switch (currentStep) {
            case STEPS.TICKET_VALIDATION:
                // If customer exists, go directly to sale details
                // If customer doesn't exist, go to registration first
                return hasExistingCustomer ? STEPS.SALE_DETAILS : STEPS.CUSTOMER_REGISTRATION;
            case STEPS.CUSTOMER_REGISTRATION:
                return STEPS.SALE_DETAILS;
            case STEPS.SALE_DETAILS:
                return STEPS.INVOICE_GENERATION;
            case STEPS.INVOICE_GENERATION:
                return STEPS.DELIVERY_OPTIONS;
            case STEPS.DELIVERY_OPTIONS:
                return STEPS.COMPLETED;
            default:
                return currentStep;
        }
    }
}

// Helper function to determine previous step
function getPreviousStep(currentStep, isExistingInvoice, hasExistingCustomer) {
    if (isExistingInvoice) {
        // Scenario 2: Existing Invoice Flow
        switch (currentStep) {
            case STEPS.DELIVERY_OPTIONS:
                return STEPS.TICKET_VALIDATION;
            default:
                return currentStep;
        }
    } else {
        // Scenario 1: New Invoice Flow
        switch (currentStep) {
            case STEPS.CUSTOMER_REGISTRATION:
                return STEPS.TICKET_VALIDATION;
            case STEPS.SALE_DETAILS:
                // If customer exists, go back to ticket validation
                // If customer was registered, go back to registration
                return hasExistingCustomer ? STEPS.TICKET_VALIDATION : STEPS.CUSTOMER_REGISTRATION;
            case STEPS.INVOICE_GENERATION:
                return STEPS.SALE_DETAILS;
            case STEPS.DELIVERY_OPTIONS:
                return STEPS.INVOICE_GENERATION;
            default:
                return currentStep;
        }
    }
}

const InvoicePortal = () => {
    const [state, dispatch] = useReducer(flowReducer, initialState);

    const handleTicketValidated = (ticketData) => {
        console.log('Ticket data received in InvoicePortal (FULL):', JSON.stringify(ticketData, null, 2));
        
        // Log specific elements of interest
        console.log('Ticket data elements:', {
            ticket: ticketData.ticket,
            isExistingInvoice: ticketData.isExistingInvoice,
            existingCustomer: ticketData.existingCustomer,
            sale: ticketData.sale
        });
        
        // Check if customer ID is 1 (indicating unregistered customer)
        const isCustomerUnregistered = 
            (ticketData.existingCustomer && ticketData.existingCustomer.id === 1) || 
            (ticketData.customer && ticketData.customer.id === 1);
        
        // First update the ticket information in state
        dispatch({
            type: ACTIONS.SET_TICKET,
            payload: {
                ticket: ticketData.ticket,
                isExistingInvoice: ticketData.isExistingInvoice,
                hasExistingCustomer: !!ticketData.existingCustomer && !isCustomerUnregistered,
                existingCustomer: isCustomerUnregistered ? null : ticketData.existingCustomer
            }
        });
        
        // Handle customer data if available and not ID=1
        if ((ticketData.existingCustomer || ticketData.customer) && !isCustomerUnregistered) {
            const customerData = ticketData.existingCustomer || ticketData.customer;
            console.log('Setting customer data:', JSON.stringify(customerData, null, 2));
            
            dispatch({
                type: ACTIONS.SET_CUSTOMER,
                payload: customerData
            });
        }
        
        // Handle sale data - always set if available, even for existing invoices
        if (ticketData.sale) {
            const saleData = {
                id: ticketData.sale.id || 0,
                sucursalId: ticketData.sale.sucursalId || 0,
                sucursalName: ticketData.sale.sucursalName || '',
                fechaVenta: ticketData.sale.fechaVenta || new Date().toISOString(),
                formaPago: ticketData.sale.formaPago || '99', // IMPORTANT: Preserve forma_pago from ticket validation
                total: ticketData.sale.total || 0,
                productos: Array.isArray(ticketData.sale.productos) ? ticketData.sale.productos : []
            };
            
            console.log('Setting sale data in InvoicePortal:', JSON.stringify(saleData, null, 2));
            console.log('Sale date verification:', saleData.fechaVenta);
            console.log('Forma pago verification:', saleData.formaPago);
            
            dispatch({
                type: ACTIONS.SET_SALE,
                payload: saleData
            });
        }
        
        // Handle invoice data for existing invoices
        if (ticketData.isExistingInvoice && ticketData.invoice) {
            console.log('Setting invoice data:', ticketData.invoice);
            
            dispatch({
                type: ACTIONS.SET_INVOICE,
                payload: ticketData.invoice
            });
        }
        
        // Move to the next step in the flow
        dispatch({ type: ACTIONS.NEXT_STEP });
    };

    const handleCustomerRegistered = (customerData) => {
        // Ensure we have valid customer data with ID
        console.log('Customer registration completed with data:', JSON.stringify(customerData, null, 2));
        
        if (!customerData.id || customerData.id === 0) {
            console.warn('Customer registered without valid ID! This may cause issues when editing.');
        }
        
        dispatch({
            type: ACTIONS.SET_CUSTOMER,
            payload: customerData
        });
        dispatch({ type: ACTIONS.NEXT_STEP });
    };

    const handleSaleDetailsConfirmed = (saleData) => {
        console.log('=== handleSaleDetailsConfirmed - RECEIVED DATA ===');
        console.log('saleData completo:', JSON.stringify(saleData, null, 2));
        console.log('saleData.formaPago:', saleData.formaPago);
        
        // Si hay datos actualizados del cliente en el objeto saleData, actualizamos el estado del cliente
        if (saleData.customer) {
            console.log('Actualizando datos del cliente en InvoicePortal:', JSON.stringify(saleData.customer, null, 2));
            dispatch({
                type: ACTIONS.SET_CUSTOMER,
                payload: saleData.customer
            });
        }
        
        // Actualizamos los datos de la venta sin incluir el cliente (que ya se actualizó por separado)
        // para evitar redundancia
        const { customer, ...saleDataWithoutCustomer } = saleData;
        console.log('=== SALE DATA WITHOUT CUSTOMER ===');
        console.log('saleDataWithoutCustomer:', JSON.stringify(saleDataWithoutCustomer, null, 2));
        console.log('saleDataWithoutCustomer.formaPago:', saleDataWithoutCustomer.formaPago);
        
        dispatch({
            type: ACTIONS.SET_SALE,
            payload: saleDataWithoutCustomer
        });
        
        dispatch({ type: ACTIONS.NEXT_STEP });
    };

    const handleInvoiceGenerated = (invoiceData) => {
        dispatch({
            type: ACTIONS.SET_INVOICE,
            payload: invoiceData
        });
        dispatch({ type: ACTIONS.NEXT_STEP });
    };

    const handleDeliveryCompleted = (deliveryData) => {
        dispatch({
            type: ACTIONS.SET_DELIVERY_METHOD,
            payload: deliveryData
        });
        dispatch({ type: ACTIONS.NEXT_STEP });
    };

    const handleError = (error) => {
        dispatch({
            type: ACTIONS.SET_ERROR,
            payload: error
        });
    };

    const handleGoBack = () => {
        // Si estamos volviendo a la pantalla de validación de ticket, limpiamos el parámetro de la URL
        if (getPreviousStep(state.step, state.isExistingInvoice, state.hasExistingCustomer) === STEPS.TICKET_VALIDATION) {
            // Eliminar el parámetro ticket de la URL sin recargar la página
            const url = new URL(window.location.href);
            url.searchParams.delete('ticket');
            window.history.replaceState({}, '', url.toString());
        }
        
        dispatch({ type: ACTIONS.PREVIOUS_STEP });
    };

    const handleStartOver = () => {
        // Eliminar el parámetro ticket de la URL sin recargar la página
        const url = new URL(window.location.href);
        url.searchParams.delete('ticket');
        window.history.replaceState({}, '', url.toString());
        
        dispatch({ type: ACTIONS.RESET_FLOW });
    };

    const renderCurrentStep = () => {
        switch (state.step) {
            case STEPS.TICKET_VALIDATION:
                return (
                    <TicketValidator
                        onTicketValidated={handleTicketValidated}
                        onError={handleError}
                    />
                );

            case STEPS.CUSTOMER_REGISTRATION:
                return (
                    <CustomerRegistration
                        sale={state.sale}
                        onCustomerRegistered={handleCustomerRegistered}
                        onError={handleError}
                        onGoBack={handleGoBack}
                    />
                );

            case STEPS.SALE_DETAILS:
                return (
                    <SaleDetails
                        sale={state.sale}
                        customer={state.customer}
                        onSaleDetailsConfirmed={handleSaleDetailsConfirmed}
                        onError={handleError}
                        onGoBack={handleGoBack}
                    />
                );

            case STEPS.INVOICE_GENERATION:
                return (
                    <InvoiceGenerator
                        sale={state.sale}
                        customer={state.customer}
                        onInvoiceGenerated={handleInvoiceGenerated}
                        onError={handleError}
                        onGoBack={handleGoBack}
                    />
                );

            case STEPS.DELIVERY_OPTIONS:
                return (
                    <DeliveryOptions
                        invoice={state.invoice}
                        customer={state.customer}
                        isExistingInvoice={state.isExistingInvoice}
                        onDeliveryCompleted={handleDeliveryCompleted}
                        onError={handleError}
                        onGoBack={!state.isExistingInvoice ? handleGoBack : null}
                    />
                );

            case STEPS.COMPLETED:
                return (
                    <div className="completion-screen">
                        <h2>¡Gracias por facturar!</h2>
                        <p>Su proceso de facturación ha sido completado exitosamente.</p>
                        <div className="completion-actions">
                            <button 
                                onClick={handleStartOver}
                                className="btn-primary"
                            >
                                Facturar otro ticket
                            </button>
                        </div>
                    </div>
                );

            default:
                return <div>Paso no reconocido</div>;
        }
    };

    const renderProgressIndicator = () => {
        if (state.step === STEPS.COMPLETED) {
            return null;
        }

        let steps;
        if (state.isExistingInvoice) {
            steps = [STEPS.TICKET_VALIDATION, STEPS.DELIVERY_OPTIONS];
        } else {
            // For new invoices, show different steps based on whether customer exists
            if (state.hasExistingCustomer) {
                steps = [STEPS.TICKET_VALIDATION, STEPS.SALE_DETAILS, STEPS.INVOICE_GENERATION, STEPS.DELIVERY_OPTIONS];
            } else {
                steps = [STEPS.TICKET_VALIDATION, STEPS.CUSTOMER_REGISTRATION, STEPS.SALE_DETAILS, STEPS.INVOICE_GENERATION, STEPS.DELIVERY_OPTIONS];
            }
        }

        const currentIndex = steps.indexOf(state.step);
        
        return (
            <div className="progress-indicator">
                {steps.map((step, index) => (
                    <div 
                        key={step}
                        className={`progress-step ${index <= currentIndex ? 'completed' : ''}`}
                    >
                        <div className="step-number">{index + 1}</div>
                        <div className="step-label">{getStepLabel(step)}</div>
                    </div>
                ))}
            </div>
        );
    };

    const getStepLabel = (step) => {
        switch (step) {
            case STEPS.TICKET_VALIDATION:
                return 'Validar Ticket';
            case STEPS.CUSTOMER_REGISTRATION:
                return 'Datos Fiscales';
            case STEPS.SALE_DETAILS:
                return 'Detalles de Venta';
            case STEPS.INVOICE_GENERATION:
                return 'Generar Factura';
            case STEPS.DELIVERY_OPTIONS:
                return 'Entrega';
            default:
                return step;
        }
    };

    return (
        <div className="invoice-portal">
            <div className="portal-header">
                <h1>Portal de Facturación Manayur</h1>
                <p>Genere su factura de manera rápida y segura</p>
            </div>

            {renderProgressIndicator()}

            {state.error && (
                <div className="error-message">
                    <h3>Error:</h3>
                    <p>{state.error}</p>
                    <button 
                        onClick={() => dispatch({ type: ACTIONS.CLEAR_ERROR })}
                        className="btn-secondary"
                    >
                        Cerrar
                    </button>
                </div>
            )}

            <div className="portal-content">
                {renderCurrentStep()}
            </div>

            <div className="portal-footer">
                <p>Sistema de facturación CFDI 4.0 cumpliendo con normativas fiscales mexicanas</p>
                <p className="version-info">Versión: {getConfig().version}</p>
            </div>
        </div>
    );
};

InvoicePortal.propTypes = {};

export default InvoicePortal;